import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { TrendingUp, Shield, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TwoFAPage = () => {
  const navigate = useNavigate();
  const { verify2FA, requires2FA, isAuthenticated } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else if (!requires2FA) {
      navigate('/login');
    }
  }, [isAuthenticated, requires2FA, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await verify2FA(code);
      toast.success('Successfully verified!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid verification code';
      setError(message);
      toast.error(message);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (value) => {
    setCode(value);
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-slide-up">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-serif text-2xl font-semibold text-[#1A4D2E]">FictoInvest</span>
          </Link>
        </div>

        {/* 2FA Card */}
        <Card className="border-stone-200 shadow-card">
          <CardHeader className="space-y-1 pb-4 text-center">
            <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#1A4D2E]" />
            </div>
            <CardTitle className="font-serif text-2xl text-[#1F2937]">
              Two-Factor Authentication
            </CardTitle>
            <CardDescription className="text-gray-600">
              Enter the 6-digit code from your authenticator app
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-center">
                <InputOTP 
                  maxLength={6} 
                  value={code} 
                  onChange={handleCodeChange}
                  data-testid="2fa-otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-12 h-14 text-xl border-stone-200" />
                    <InputOTPSlot index={1} className="w-12 h-14 text-xl border-stone-200" />
                    <InputOTPSlot index={2} className="w-12 h-14 text-xl border-stone-200" />
                    <InputOTPSlot index={3} className="w-12 h-14 text-xl border-stone-200" />
                    <InputOTPSlot index={4} className="w-12 h-14 text-xl border-stone-200" />
                    <InputOTPSlot index={5} className="w-12 h-14 text-xl border-stone-200" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full bg-[#1A4D2E] hover:bg-[#143d24] text-white shadow-lg shadow-green-900/20 py-5 disabled:opacity-50"
                data-testid="2fa-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Verifying...
                  </span>
                ) : (
                  'Verify'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm font-medium text-[#1A4D2E] hover:underline"
              >
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-center text-gray-500">
          Open your authenticator app (Google Authenticator, Authy, etc.) to get your code.
        </p>
      </div>
    </div>
  );
};

export default TwoFAPage;
